import React, { useEffect, useMemo, useState } from "react"
import { EnumTelaIdentificacao, useIdentificacaoFragment } from "./components/use-identificacao-fragment"
import { Box, Grid, Typography } from "@material-ui/core"
import { useDefaultCadastroStyles } from "components/cadastros/components/default-cadastro-styles"
import classNames from "classnames"
import { useStyles } from "./identificacao-fragment-styles"
import { Stepper } from "components/stepper"
import { useStepperStyles } from "components/stepper/stepper-styles"
import { PessoaModelForm } from "api/cardapio/models/pessoa/pessoa-model"
import { useThemeQueries } from "components/dialogs/dialog-adicionar-produto-com-subitem/util-styles"
import { toInteger } from "lodash"
import { getIDFromUrl } from "utils/get-id-url"

interface IdentificacaoFragmentProps {
    solicitarCliente: string
    submitNovaPessoa: (model: PessoaModelForm) => Promise<void>
    isDelivery: boolean
    empresaId: string
    faseIdentificacao: boolean
}

export const IdentificacaoFragment = ({ solicitarCliente, submitNovaPessoa, isDelivery, empresaId, faseIdentificacao }: IdentificacaoFragmentProps) => {

    const classesDefault = useDefaultCadastroStyles()
    const { isMobile } = useThemeQueries()
    const classes = useStyles()
    const stepperClasses = useStepperStyles();
    const mesaId = getIDFromUrl('mesaId')

    const soliClientesPorModeloTrabalho = useMemo(() => {
        return Number(solicitarCliente)
    }, [solicitarCliente])

    const steps = useMemo(() => {
        const filterSteps = [
            EnumTelaIdentificacao.Telefone,
            EnumTelaIdentificacao.CpfCnpj,
            EnumTelaIdentificacao.Nome,
            EnumTelaIdentificacao.Email,
            EnumTelaIdentificacao.DataNascimento,
            EnumTelaIdentificacao.Sexo,
        ].filter(step => Boolean(step & soliClientesPorModeloTrabalho))

        if (isDelivery) {
            if (!filterSteps.some(step => step === EnumTelaIdentificacao.Telefone)) {
                filterSteps.unshift(EnumTelaIdentificacao.Telefone)
            }
            if (!filterSteps.some(step => step === EnumTelaIdentificacao.Nome)) {
                filterSteps.push(EnumTelaIdentificacao.Nome)
            }
        }
        return filterSteps
    }, [isDelivery, soliClientesPorModeloTrabalho])

    const { formArray, formStepper } = useIdentificacaoFragment({
        soliClientesPorModeloTrabalho,
        submitNovaPessoa,
        isDelivery,
        steps,
        empresaId,
        faseIdentificacao
    })

    const getButtons = useMemo(() => {
        return (
            <div className={stepperClasses.stepButtons}>
                <Box>
                    <Box display="flex" gridGap={8}>
                        {formArray[formStepper.currentStep]?.previousButton && formStepper.currentStep !== 0 && (
                            <Grid item xs>
                                {formArray[formStepper.currentStep].previousButton}
                            </Grid>
                        )}
                        {formArray[formStepper.currentStep]?.nextButton && (
                            <Grid
                                item
                                xs
                            >
                                {formArray[formStepper.currentStep]?.nextButton}
                            </Grid>
                        )}
                    </Box>
                </Box>
            </div>
        );
    }, [stepperClasses.stepButtons, formArray, formStepper]);


    const [, setViewportHeight] = useState<number | string>(typeof window !== 'undefined' ? window?.visualViewport?.height ? toInteger(window?.visualViewport?.height) + 20 + 'px' : '100%' : 0)

    useEffect(() => {
        const handleResize = () => {
            setViewportHeight(window?.visualViewport?.height ? toInteger(window?.visualViewport?.height) + 20 + 'px' : '100%')
        }
        window.addEventListener('resize', handleResize)
        const vv: any = (window as any).visualViewport
        const handleVV = () => {
            if (vv?.height) setViewportHeight(vv.height)
        }
        if (vv) vv.addEventListener('resize', handleVV)
        return () => {
            window.removeEventListener('resize', handleResize)
            if (vv) vv.removeEventListener('resize', handleVV)
        }
    }, [])


    const renderIdentificarClienteEtapas = () => {

        return (
            <Box className={classesDefault.pagesContainer} style={{ maxHeight: window?.visualViewport?.height && window?.visualViewport?.height < 500 ? mesaId ? '305px' : '270px' : '100%', minHeight: 270 }}>
                <Box className={classNames(classesDefault.keyboardPage, classesDefault.fullWidth)}>
                    <div className={classes.content}>
                        <div
                            className={classNames(classes.contentForms)}
                        >
                            {steps.length > 1 && (
                                <div className={classNames(stepperClasses.stepper, steps.length <= 2 && !isMobile ? undefined : classes.stepper)}>
                                    <Stepper
                                        style={{ padding: (steps.length <= 2 && !isMobile) ? undefined : 0 }}
                                        steps={formArray}
                                        stepAtual={formStepper.currentStep}
                                    />
                                </div>
                            )}
                            <div className={classNames(stepperClasses.step, classes.stepContent)}>
                                <div className={stepperClasses.stepTitleContent}>
                                    <Box className={classes.iconStep}>
                                        {formArray[formStepper.currentStep].formTitleIcon}
                                    </Box>
                                    <Typography
                                        align="center"
                                        className={stepperClasses.stepTitle}
                                        variant="h4"
                                        color="primary"
                                    >
                                        {formArray[formStepper.currentStep].formTitle}
                                    </Typography>
                                    <Box>
                                        <Typography align="center" variant="body1">
                                            {formArray[formStepper.currentStep].formDesc}
                                        </Typography>
                                    </Box>
                                </div>
                            </div>
                            <Box flex style={{ height: "100%", width: '100%', display: 'flex', justifyContent: 'center' }}>
                                <div className={classNames(stepperClasses.stepFix, classes.stepFix)}>
                                    <div
                                        className={classNames(
                                            stepperClasses.step,
                                            stepperClasses.stepContent,
                                            classes.step
                                        )}
                                    >
                                        <div className={classNames(stepperClasses.stepForm, classes.stepForm)}>
                                            {formArray[formStepper.currentStep].form}
                                        </div>
                                    </div>
                                </div>
                            </Box>
                        </div>
                        <div className={classes.acoes}>
                            {!isDelivery && (
                                <Box mb={2}>
                                    <Typography variant="body2" align="center">
                                        Todos os campos são opcionais
                                    </Typography>
                                </Box>
                            )}
                            {getButtons}
                        </div>
                    </div>
                </Box>
            </Box>
        )
    }

    return (
        <>
            {renderIdentificarClienteEtapas()}
        </>
    )
}